import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { formatDisplayDateTime } from '../../lib/dateDisplay';
import { AppHeader } from '../../components/common/LeftMenu';
import { history } from '../../history';
import { NotificationAction } from '../../actions/NotificationAction';
import { Modal } from 'react-bootstrap';
import { Translate as t } from '../../config/Translate';
import { getLicenseRole } from '../../lib/licenseHelper';
import { evaluateRouteAccess } from '../../lib/rbacGuard';

import {
	LeadingActions,
	SwipeableList,
	SwipeableListItem,
	SwipeAction,
	TrailingActions,
	Type as ListType,
} from 'react-swipeable-list';
import 'react-swipeable-list/dist/styles.css';
import { NoRecord } from '../HomePage';
import { useConfirmDialog } from '../../components/common/ConfirmDialog';
import { Loading } from '../../components/common/Loading';

// F-029: AuthRoute.js 의 getCurrentUser 와 동일 패턴 — JSON 파싱 실패·부재 시 null.
const getCurrentUser = () => {
	try {
		const raw = localStorage.getItem('user');
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
};

const NotyCard = ({ item, readCallback }) => {

	const excepts = ["updater", "updateDate", "scheId"];
	const getIcon = () => {
		switch (item.notiType) {
			case "COMMENT": return <div className="icon-box bg-primary float-start"><ion-icon name="chatbubble-outline"></ion-icon></div>;
			case "SCHEDULE": return <div className="icon-box bg-success float-start"><ion-icon name="calendar-outline"></ion-icon></div>;
			case "FAIL": return <div className="icon-box bg-danger float-start"><ion-icon name="close-outline"></ion-icon></div>;
			case "DOCUMENT": return <div className="icon-box bg-info float-start"><ion-icon name="close-outline"></ion-icon></div>;
			case "WARNING": return <div className="icon-box bg-warning float-start"><ion-icon name="alert-outline"></ion-icon></div>;
			case "INFO": return <div className="icon-box bg-info float-start"><ion-icon name="information-outline"></ion-icon></div>;
		}
	}

	const goLink = () => {
		readCallback(item);
	}

	const fromCamel = (v) => {
		const d = v.replace(/([a-z])([A-Z])/g, '$1 $2');
		return d.charAt(0).toUpperCase() + d.slice(1);
	}

	const kVal = (key, val) => {
		if (key.toUpperCase().indexOf("DATE") > -1)
			return formatDisplayDateTime(val, { seconds: false, fallback: String(val ?? '') });
		if (key === 'status' && val) {
			const i18nKey = 'service.status.' + val.toLowerCase();
			const translated = t(i18nKey);
			return translated !== i18nKey ? translated : val;
		}
		return val;
	}

	const getMessage = (msg) => {
		try {
			const json = JSON.parse(msg);
			const arr = [];
			Object.keys(json).forEach(function (key) {
				if (!excepts.includes(key))
					arr.push(fromCamel(key) + " : " + kVal(key, json[key]));
			})
			return arr.join(", ");
		} catch (e) {
			return msg;
		}
	}

	const getTitle = (title) => {
		if (!title) return title;
		const key = title.toLowerCase();
		const statusKey = 'service.status.' + key;
		const statusVal = t(statusKey);
		if (statusVal !== statusKey) return statusVal;
		const flatVal = t(key);
		return flatVal !== key ? flatVal : title;
	}

	return (
		<li className={item.isRead ? "p-2" : "active p-2"}>
			<a onClick={() => goLink()} className="item">
				{getIcon()}
				<div className="in">
					<div>
						<div className="mb-05"><strong>{getTitle(item.title)}</strong></div>
						<div className="text-small mb-05">{getMessage(item.content)}</div>
						<div className="text-xsmall">{formatDisplayDateTime(item?.createDate)}</div>
					</div>
					{!item.isRead && <div className="swipeable-list-right"><span className="badge badge-primary badge-empty"></span></div>}
				</div>
			</a>
		</li>
	)
}

const MyNotification = ({ list, GetAll, Read, UnRead, Delete, ReadAll, DeleteRead, DeleteAll, isLoading }) => {
	const { confirm, dialog } = useConfirmDialog();

	//const [t, i18n] = useTranslation();

	const [isAdmin, setIsAdmin] = useState(location.pathname.toLowerCase().startsWith('/admin/'));
	const [showAS, setShowAS] = useState(false);
	const [asType, setASType] = useState("");
	const [hasNew, setHasNew] = useState(false);
	// F-029: 목적지가 RBAC 화이트리스트 밖일 때 안내 배너.
	const [accessNotice, setAccessNotice] = useState(false);
	// 첫 목록 조회 중 표시 — NotificationReducer.isLoading 은 읽음·삭제와 공유돼 쓰지 않는다.
	const [fetching, setFetching] = useState(true);
	const aliveRef = useRef(true);

	useEffect(() => {
		setShowAS(false);
		setHasNew(list.filter(x => !x.isRead).length > 0);
	}, [isLoading])

	useEffect(() => {
		setHasNew(list.filter(x => !x.isRead).length > 0);
	}, [list]);

	useEffect(() => {
		aliveRef.current = true;
		Promise.resolve(GetAll()).finally(() => {
			if (aliveRef.current) setFetching(false);
		});
		return () => { aliveRef.current = false; };
	}, []);

	const read = (item) => {
		const dest = "/" + (isAdmin ? "Admin/Admin" + item?.type : item?.type) + "/" + item?.refKey;
		const user = getCurrentUser();
		const role = getLicenseRole(user);
		const access = evaluateRouteAccess({ role, permAdminAccess: user?.permAdminAccess, pathname: dest });

		if (access?.allow === false) {
			console.warn('[guard] notification destination blocked', {
				handler: 'MyNotification.read',
				callerCompanyId: user?.companyId,
				callerRole: role,
				targetDest: dest,
				targetId: item?._id,
				targetRefKey: item?.refKey,
				reason: 'destination outside rbac whitelist for role',
			});
			setAccessNotice(true);
			return;
		}

		Read(item._id);
		history.push(dest);
	}

	const leadingActions = ({ item }) => (
		<LeadingActions>
			<SwipeAction onClick={() => item.isRead ? UnRead(item._id) : Read(item._id)}>
				<div className="bg-primary text-center" style={{ width: "80px" }}>
					<ion-icon size="large" name={item.isRead ? "mail-outline" : "mail-unread-outline"}></ion-icon>
				</div>
			</SwipeAction>
		</LeadingActions>
	);

	const trailingActions = ({ item }) => (
		<TrailingActions>
			<SwipeAction onClick={() => Delete(item._id)} destructive={true}>
				<div className="bg-danger text-center" style={{ width: "80px" }}>
					<ion-icon name="trash-outline"></ion-icon>
				</div>
			</SwipeAction>
		</TrailingActions>
	);

	const AS = (type) => {
		setASType(type);
		setShowAS(true);
	}

	return (
		<>
			{dialog}
			{!isAdmin && <div className="appHeader no-border bg-white">
				<AppHeader title={t('notification.title')} />
				<div className="right">
					{/* IMP-445: 관리 트리거 아이콘은 어드민 상단바(AdminTopMenu 의 `isNotificationPage && !hideMenu` 블록)와 동일하게 ellipsis 로 통일.
					    이 화면에는 별도 벨이 없어 미읽음 배지는 이 버튼에 유지한다. */}
					{!fetching && list.length > 0 && <a onClick={() => AS('read')} className="headerButton" aria-label={t('notification.button.manage')}>
						<ion-icon name="ellipsis-vertical-outline"></ion-icon>
						{hasNew && <span className="badge badge-primary badge-empty"></span>}
					</a>}
					<a href="#!" className="headerButton" aria-label={t('common.button.close')} onClick={(e) => { e.preventDefault(); history.goBack(); }}>
						<ion-icon name="close-outline"></ion-icon>
					</a>
				</div>
			</div>}

			<div id="appCapsule" className="full-height">
				{accessNotice && <div className="alert alert-danger m-2">{t('common.message.noAccess')}</div>}
				{fetching && <div className="mt-3"><Loading /></div>}
				{!fetching && list.length === 0 && <NoRecord title={t('notification.message.noNotification')} contents={t('notification.message.notFound')} />}
				{!fetching && list.length > 0 && <div className="section full">
					<ul className="listview image-listview flush notification-list">
						<SwipeableList type={ListType.ANDROID} fullSwipe={true}>
							{list.sort((a, b) => b.createDate - a.createDate).map((item, index) =>
								<SwipeableListItem key={item._id}
									fullSwipe={false}
									leadingActions={leadingActions({ item })}
									trailingActions={trailingActions({ item })}>
									<NotyCard item={item} readCallback={(item) => read(item)} />
								</SwipeableListItem>
							)}
						</SwipeableList>
					</ul>
				</div>
				}
			</div>

			<Modal show={showAS} className="action-sheet" dialogClassName="menu-all">
				<Modal.Body>
					<div className="border-bottom p-2">
						<h2 className="modal-title text-left">
							{t('notification.button.manage')}
						</h2>
					</div>
					<ul className="listview flush transparent image-listview mt-1 no-line">
						<li>
							<a href className="item" onClick={() => ReadAll()}>
								<div className="icon-box text-primary">
									<ion-icon name="mail-unread-outline"></ion-icon>
								</div>
								<div className="in">{t('notification.button.readAll')}</div>
							</a>
						</li>
						<li className="border-top">
							<a href className="item" onClick={async () => {
								setShowAS(false);
								if (await confirm({ title: t('common.button.deleteAll'), message: t('common.message.confirm.delete'), danger: true })) DeleteAll();
							}}>
								<div className="icon-box text-danger">
									<ion-icon name="trash-outline"></ion-icon>
								</div>
								<div className="in">{t('common.button.deleteAll')}</div>
							</a>
						</li>
						<li className="border-top">
							<a href className="item" onClick={async () => {
								setShowAS(false);
								if (await confirm({ title: t('notification.button.deleteRead'), message: t('common.message.confirm.delete'), danger: true })) DeleteRead();
							}}>
								<div className="icon-box text-danger">
									<ion-icon name="trash-outline"></ion-icon>
								</div>
								<div className="in">{t('notification.button.deleteRead')}</div>
							</a>
						</li>
						<li className="border-top">
							<a href className="item" onClick={() => setShowAS(false)}>
								<div className="icon-box text-danger">
									<ion-icon name="exit-outline"></ion-icon>
								</div>
								<div className="in">{t('common.button.cancel')}</div>
							</a>
						</li>
					</ul>
				</Modal.Body>
			</Modal>
		</>
	);
};

const mapState = (state) => {
	const list = state.NotificationReducer.list;
	const isLoading = state.NotificationReducer.isLoading;
	return { list, isLoading };
}

const mapDispatch = (dispatch) => ({
	GetAll: () => dispatch(NotificationAction.GetAll()),
	Read: (_id) => dispatch(NotificationAction.Read(_id)),
	UnRead: (_id) => dispatch(NotificationAction.UnRead(_id)),
	Delete: (_id) => dispatch(NotificationAction.Delete(_id)),
	ReadAll: () => dispatch(NotificationAction.ReadAll()),
	DeleteRead: () => dispatch(NotificationAction.DeleteRead()),
	DeleteAll: () => dispatch(NotificationAction.DeleteAll()),
});

export default connect(mapState, mapDispatch)(MyNotification);